import React, { useState, useCallback, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { getProjectBySlug as staticGet } from "@/data/projects";
import { getProjectBySlug as dbGet, GalleryProjectRow } from "@/lib/api";
import "@/styles/pages.css";
import "@/components/Floaters/floaters.css";

const ytId = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

interface Resolved {
  title: string; category: string; hero: string;
  paragraph1: string; paragraph2: string;
  photos: string[]; youtube?: string | null; isHtml: boolean;
}

const fromStatic = (slug?: string): Resolved | null => {
  const p = staticGet(slug); if (!p) return null;
  return { title: p.title, category: p.category, hero: p.hero, paragraph1: p.paragraph1, paragraph2: p.paragraph2, photos: p.photos, youtube: p.youtube, isHtml: false };
};
const fromDb = (r: GalleryProjectRow): Resolved => ({
  title: r.title, category: r.category || "", hero: r.hero_image_url || "",
  paragraph1: r.description_1 || "", paragraph2: r.description_2 || "",
  photos: r.photos || [], youtube: r.youtube_url, isHtml: true,
});

const ProjectDetail: React.FC = () => {
  useReveal();
  const { slug } = useParams();
  const [project, setProject] = useState<Resolved | null | undefined>(undefined);
  const [lb, setLb] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!slug) { setProject(null); return; }
    dbGet(slug).then(row => {
      if (cancelled) return;
      if (row) setProject(fromDb(row));
      else setProject(fromStatic(slug));
    }).catch(() => setProject(fromStatic(slug)));
    return () => { cancelled = true; };
  }, [slug]);

  const close = useCallback(() => setLb(null), []);
  const next = useCallback(() => setLb(i => i === null ? 0 : (i + 1) % (project?.photos.length || 1)), [project]);
  const prev = useCallback(() => setLb(i => i === null ? 0 : (i - 1 + (project?.photos.length || 1)) % (project?.photos.length || 1)), [project]);

  useEffect(() => {
    if (lb === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lb, close, next, prev]);

  if (project === undefined) return <main className="iav-page" style={{ minHeight: "100vh" }} />;
  if (project === null) return <Navigate to="/gallery" replace />;
  const yid = ytId(project.youtube);

  return (
    <main className="iav-page" data-testid="project-detail">
      <section className="iav-pd-hero" data-testid="project-hero">
        {project.hero && <img src={project.hero} alt={project.title} />}
        <div className="iav-pd-meta">
          <span className="iav-pd-cat" data-reveal>{project.category}</span>
          <h1 className="iav-pd-title" data-reveal>{project.title}</h1>
        </div>
      </section>

      <div className="iav-pd-body">
        {project.isHtml ? (
          <>
            <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: project.paragraph1 }} />
            <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: project.paragraph2 }} />
          </>
        ) : (
          <>
            <p data-reveal>{project.paragraph1}</p>
            <p data-reveal>{project.paragraph2}</p>
          </>
        )}
      </div>
      <div className="iav-pd-cta">
        <Link to="/quote" className="iav-btn iav-btn-primary">Get a Quote</Link>
        <Link to="/gallery" className="iav-btn iav-btn-outline">Back to Gallery</Link>
      </div>

      {project.photos.length > 0 && (
        <div className="iav-pd-photos">
          {project.photos.map((p, i) => (
            <button key={i} className="iav-pd-photo" onClick={() => setLb(i)} aria-label={`Open photo ${i + 1}`} data-cursor="cta">
              <img src={p} alt={`${project.title} photo ${i + 1}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {yid && (
        <section className="iav-pd-yt">
          <h2>Watch the Show</h2>
          <iframe className="iav-pd-yt-frame" src={`https://www.youtube.com/embed/${yid}`} title={`${project.title} video`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </section>
      )}

      {lb !== null && (
        <div className="iav-lb" onClick={close} role="dialog">
          <button className="iav-lb-close" onClick={(e) => { e.stopPropagation(); close(); }} aria-label="Close">✕</button>
          <button className="iav-lb-arrow iav-lb-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">‹</button>
          <button className="iav-lb-arrow iav-lb-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">›</button>
          <img className="iav-lb-img" src={project.photos[lb]} alt={`Photo ${lb + 1}`} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </main>
  );
};
export default ProjectDetail;
